import { TaskData, LaunchRequest, LaunchResponse } from './types';
import { handleApiResponse } from '../common';
import { getApiBaseUrl, API_VERSION } from '../index';

/**
 * Tasks API - launch DCCs and manage tasks
 */
export const tasksApi = {
  /**
   * Launch a DCC application on a machine
   * @param {Object} options
   * @param {string} options.launcherName - Launcher name (e.g., '3dsmax')
   * @param {string} options.profileName - Profile name to launch
   * @param {string} options.machineHostname - Target machine hostname
   * @param {string[]} options.additionalArgs - Additional CLI args (optional)
   * @param {Object} options.environmentOverrides - Extra env vars (optional)
   * @param {string} options.requestedBy - User requesting the launch (optional)
   * @returns {Promise<LaunchResponse>}
   */
  async launch({ 
    launcherName, 
    profileName, 
    machineHostname, 
    additionalArgs = null,
    environmentOverrides = null,
    requestedBy = null 
  }) {
    try {
      const request = new LaunchRequest({
        launcher_name: launcherName,
        profile_name: profileName,
        machine_hostname: machineHostname,
        additional_args: additionalArgs,
        environment_overrides: environmentOverrides,
        requested_by: requestedBy
      });

      const response = await fetch(
        `${getApiBaseUrl()}${API_VERSION}/tasks/launch`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(request.toApiPayload())
        }
      );

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();

      return LaunchResponse.fromApiResponse(data);
    } catch (error) {
      console.error('Failed to launch DCC:', error);
      throw error;
    }
  },

  /**
   * Get task status by ID
   * @param {number} taskId
   * @returns {Promise<TaskData>}
   */
  async getTask(taskId) {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}${API_VERSION}/tasks/${taskId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();

      return TaskData.fromApiResponse(data.task);
    } catch (error) {
      console.error(`Failed to get task ${taskId}:`, error);
      throw error;
    }
  },

  /**
   * List tasks with optional filters
   * @param {Object} options
   * @param {string} options.machineHostname - Filter by machine
   * @param {string} options.status - Filter by status
   * @param {number} options.limit - Max results
   * @returns {Promise<{tasks: TaskData[], total: number}>}
   */
  async list({ machineHostname = null, status = null, limit = 50 } = {}) {
    try {
      const params = new URLSearchParams();
      if (machineHostname) params.append('machine_hostname', machineHostname);
      if (status) params.append('status', status);
      if (limit) params.append('limit', limit.toString());

      const queryString = params.toString();
      const url = `${getApiBaseUrl()}${API_VERSION}/tasks${queryString ? '?' + queryString : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();

      return {
        tasks: (data.tasks || []).map(TaskData.fromApiResponse),
        total: data.total || 0
      };
    } catch (error) {
      console.error('Failed to list tasks:', error);
      throw error;
    }
  }
};

export default tasksApi;

